import moment from "moment/moment";
import { timezone } from "@utils/StringConstants";
import momentTz from "moment-timezone";
import { updateBackUrl } from "@utils/getLocalStorage";
import { find } from "lodash";

export function isOdd(num: number): boolean {
  return num % 2 !== 0;
}
export function getTimeDifference(time1: number, time2: number, unit: "seconds" | "minutes"): number {
  const diff = Math.abs(time1 - time2);
  const factor = unit === "seconds" ? 1000 : 60000;

  return Math.floor(diff / factor);
}

export function numberToString(number: number): string {
  return number.toLocaleString("en-US");
}

export function numberToWord(number: number): string {
  if (number < 0 || number > 100) {
    return `${number}`;
  }

  const ones = [
    "zero",
    "first",
    "second",
    "third",
    "fourth",
    "fifth",
    "sixth",
    "seventh",
    "eighth",
    "ninth",
    "tenth",
    "eleventh",
    "twelfth",
    "thirteenth",
    "fourteenth",
    "fifteenth",
    "sixteenth",
    "seventeenth",
    "eighteenth",
    "nineteenth",
  ];
  const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

  number++;

  if (number < 20) {
    return ones[number].charAt(0).toUpperCase() + ones[number].slice(1);
  }

  const digit = number % 10;
  const ten = Math.floor(number / 10);

  return `${tens[ten]}${digit === 0 ? "" : "-" + ones[digit].charAt(0).toUpperCase() + ones[digit].slice(1)}`;
}

export const sortString = (a: any, b: any, fieldName: string) => {
  a[fieldName] = a[fieldName] ? a[fieldName] : "";
  b[fieldName] = b[fieldName] ? b[fieldName] : "";
  const fieldA = a[fieldName].toUpperCase(); // ignore upper and lowercase
  const fieldB = b[fieldName].toUpperCase(); // ignore upper and lowercase
  // const fieldA = a[fieldName].trim(); // case sensitive
  // const fieldB = b[fieldName].trim(); // case sensitive
  if (fieldA < fieldB) {
    return -1;
  }
  if (fieldA > fieldB) {
    return 1;
  }
  return 0;
};

export const isReviewerApproved = (email: any, reviewers: any[], data: any) => {
  let { status, currentRoutingOrder } = data;
  let disabled = false;
  if (["expired", "rejected", "completed"].includes(status)) {
    disabled = true;
    return disabled;
  }

  if (reviewers && reviewers.length > 0) {
    let isPendingWithMe = find(reviewers, {
      reviewingOrder: currentRoutingOrder,
      status: "In-Progress",
      emailAddress: email,
    });
    disabled = !isPendingWithMe;
  }
  return disabled;
};

export const getFormattedDate = (date: any) => {
  if (date) {
    return momentTz(date).tz(timezone).format("DD MMM YYYY hh:mm:ss A");
  }
  return date;
};

export const switchHasRole = (records: any, key: string) => {
  if (records?.roles?.includes(key)) {
    return true;
  }
  return false;
};

function convertMonthsToMonthsAndDays(monthsDecimal: any) {
  const months = Math.floor(monthsDecimal);
  const remainingDaysDecimal = monthsDecimal - months;
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const year = new Date().getFullYear();
  if (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) {
    daysInMonth[1] = 29;
  }
  const remainingDays = Math.round(remainingDaysDecimal * daysInMonth[months]);
  return `${months} ${months > 1 ? "Months" : "Month"} and ${remainingDays} ${remainingDays > 1 ? "Days" : "Day"}`;
}

export const monthChange = (commencementDate: any, completionDate: any) => {
  if (commencementDate && completionDate) {
    const duration = moment.duration(moment(completionDate, "YYYY-MM-DD").diff(moment(commencementDate, "YYYY-MM-DD")));
    const years = duration.years();
    const months = duration.months();
    const days = duration.days();
    let result: string = "";
    if (years !== 0) {
      result += `${years} ${years !== 1 ? "years" : "year"}, `;
    }
    if (months !== 0) {
      result += `${months} ${months !== 1 ? "months" : "month"}, `;
    }
    if (days !== 0) {
      result += `${days} ${days !== 1 ? "days" : "day"}, `;
    }
    result = result.replace(/, $/, "");
    return result;
  }
};

export const scrollTop = () => {
  setTimeout(() => {
    let element: any = document.getElementById("contentWrapperContent");
    if (element) {
      const parentElement: any = element.parentNode;
      parentElement.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      // parentElement.scrollIntoView({
      //   behavior: "smooth",
      //   block: "start",
      //   inline: "nearest",
      // });
    }
  }, 0);
};

export const scrollToElement = (id: string) => {
  setTimeout(() => {
    let element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, 0);
};

export const getTotalCost = (cost: any) => {
  let temp: any = cost.replace(/,/gi, "");
  return Number(temp);
};

export const getUrlwithParams = () => {
  const currentURL = window.location.href;
  const { pathname, search } = new URL(currentURL);
  updateBackUrl(`${pathname}${search}`);
  return `${pathname}${search}`;
};

export const hasKeyString = (key: string) => {
  if (window.location.pathname.split("/")[1].split("-").includes(key)) {
    return true;
  } else {
    return false;
  }
};

export const getKey = (currentPage: number, index: number) => {
  return currentPage * 10 + index + 1;
};

export const formatDocVersion = (value: any): string => {
  if (value === null || value === undefined || value === "") return "-";
  try {
    const s = String(value).trim();
    if (s.startsWith("V") || s.startsWith("v")) {
      return s.toUpperCase();
    }
    if (!s.includes(".")) {
      return `V${Number(s)}`;
    }
    const [major, minor] = s.split(".").map(Number);
    const version = ((major || 1) - 1) * 10 + (minor || 0);
    return `V${version}`;
  } catch (e) {
    return `V${value}`;
  }
};

export const parseDocVersionNumber = (value: any): number => {
  if (value === null || value === undefined || value === "") return 0;
  try {
    const s = String(value).replace(/^[Vv]/, "").trim();
    if (s.includes(".")) {
      const [major, minor] = s.split(".").map(Number);
      return ((major || 1) - 1) * 10 + (minor || 0);
    }
    const num = Number(s);
    return isNaN(num) ? 0 : num;
  } catch (e) {
    return 0;
  }
};
